import {
  ArrowLeftIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  StarIcon,
} from "@heroicons/react/24/outline";
import {
  CheckCircleIcon,
  HeartIcon as HeartIconSolid,
} from "@heroicons/react/24/solid";
import {
  ActionIcon,
  Alert,
  AppShell,
  Badge,
  Button,
  Center,
  Container,
  Grid,
  Group,
  Loader,
  Modal,
  Paper,
  Rating,
  rem,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FeatureUnavailableBanner } from "../components/common/FeatureUnavailableBanner";
import { ManagerOrAdmin } from "../components/common/RoleBasedAccess";
import { Header } from "../components/layout/Header";
import { ROUTES } from "../constants/routes";
import { useCurrentUser } from "../hooks/queries/useCurrentUser";
import { useFeatureFlags } from "../hooks/queries/useFeatureFlags";
import { Person } from "../models/person.model";
import {
  BulkConfirmSkillAssessmentsRequest,
  CreateSkillConfirmationRequest,
  SkillConfirmationStatus,
  SkillMatrixEntry,
} from "../models/skill-matrix.model";
import { TechnologyExperience } from "../models/technologyExperience.model";
import { getUserRole } from "../models/user.model";
import { PersonService } from "../services/personService";
import { ProjectService } from "../services/projectService";
import {
  CreateSkillAssessmentAsManagerRequest,
  SkillMatrixService,
} from "../services/skillMatrixService";

export function PersonSkills() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [skills, setSkills] = useState<SkillMatrixEntry[]>([]);
  const [filteredSkills, setFilteredSkills] = useState<SkillMatrixEntry[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 1200); // 1.2 seconds debounce for better user experience
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [person, setPerson] = useState<Person | null>(null);
  const [currentPersonId, setCurrentPersonId] = useState<number | null>(null);
  const [noSkillsFound, setNoSkillsFound] = useState(false);
  const [sortBy, setSortBy] = useState<
    "technology" | "category" | "skillLevel" | "yearsExperience" | "status"
  >("technology");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [activeTab, setActiveTab] = useState<string | null>("all");

  // Manager assessment modal state
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [assessmentSkill, setAssessmentSkill] =
    useState<SkillMatrixEntry | null>(null);
  const [assessmentMode, setAssessmentMode] = useState<
    "create" | "edit" | "confirm"
  >("create");
  const [assessmentFormSkillLevel, setAssessmentFormSkillLevel] =
    useState<number>(1);
  const [assessmentFormYearsExperience, setAssessmentFormYearsExperience] =
    useState<number | null>(null);
  const [assessmentFormYearsInput, setAssessmentFormYearsInput] = useState("");
  const [assessmentFormComments, setAssessmentFormComments] = useState("");
  const [managerComments, setManagerComments] = useState("");
  const [isProcessingAssessment, setIsProcessingAssessment] = useState(false);

  // Bulk confirmation state
  const [isBulkConfirming, setIsBulkConfirming] = useState(false);
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);
  const [bulkManagerComments, setBulkManagerComments] = useState("");

  const skillMatrixService = new SkillMatrixService();
  const personService = new PersonService();
  const projectService = new ProjectService();
  const { data: currentUser, isLoading: userLoading } = useCurrentUser();
  const { data: featureFlags } = useFeatureFlags();
  const skillAssignmentEnabled = featureFlags?.enableSkillAssignment ?? true;

  // Technology experience from projects
  const [technologyExperience, setTechnologyExperience] = useState<TechnologyExperience[]>([]);

  // Check if current user is manager or admin
  const isManagerOrAdmin = currentUser && (currentUser.isManager || currentUser.isAdmin);
  const userRole = currentUser ? getUserRole(currentUser) : null;

  const loadData = async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);
    setNoSkillsFound(false);

    try {
      const personId = parseInt(id);

      // Get person details, skills, and technology experience in parallel
      const [personData, userSkills, techExperience] = await Promise.all([
        personService.getPersonById(personId),
        skillMatrixService.getSkillMatrixByPerson(personId).catch((err) => {
          // Handle 404 error specifically (no skills found)
          if (err instanceof Error && err.message.includes("404")) {
            setNoSkillsFound(true);
            return [];
          }
          throw err;
        }),
        projectService.getTechnologyExperienceByPerson(personId).catch((err) => {
          console.error("Error fetching technology experience:", err);
          return []; // Return empty array if technology experience fails
        }),
      ]);

      if (currentUser?.personId) {
        setCurrentPersonId(currentUser.personId);
      }
      setPerson(personData);
      setSkills(userSkills);
      setFilteredSkills(userSkills);
      setTechnologyExperience(techExperience);

      if (userSkills.length === 0) {
        setNoSkillsFound(true);
      }
    } catch (err) {
      console.error("Error loading person skills data:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load data. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser && !userLoading) {
      loadData();
    }
  }, [id, currentUser, userLoading]);

  // Reset tab to 'all' if non-manager user has a manager-only tab selected
  useEffect(() => {
    if (currentUser && !isManagerOrAdmin && (activeTab === "pending" || activeTab === "no-assessment")) {
      setActiveTab("all");
    }
  }, [currentUser, isManagerOrAdmin, activeTab]);

  const getFilteredSkillsByTab = useCallback(() => {
    let filtered = skills;

    // Filter by tab
    if (activeTab === "pending") {
      filtered = skills.filter((skill) => skill.needsConfirmation);
    } else if (activeTab === "confirmed") {
      filtered = skills.filter((skill) => skill.isConfirmed);
    } else if (activeTab === "no-assessment") {
      filtered = skills.filter((skill) => !skill.hasAssessment);
    }

    // Apply search and category filters
    filtered = filtered.filter((skill) => {
      const matchesSearch = skill.technologyName
        .toLowerCase()
        .includes(debouncedSearchQuery.toLowerCase());
      const matchesCategory = !selectedCategory || selectedCategory === "all";
      return matchesSearch && matchesCategory;
    });

    return filtered;
  }, [skills, activeTab, debouncedSearchQuery, selectedCategory]);

  const sortSkills = useCallback(() => {
    const filtered = getFilteredSkillsByTab();

    const sortedSkills = [...filtered];
    sortedSkills.sort((a, b) => {
      let comparison = 0;

      if (sortBy === "technology") {
        comparison = a.technologyName.localeCompare(b.technologyName);
      } else if (sortBy === "category") {
        comparison = a.technologyName.localeCompare(b.technologyName); // No categories in v2
      } else if (sortBy === "skillLevel") {
        comparison = (a.levelSelfRating || 0) - (b.levelSelfRating || 0);
      } else if (sortBy === "yearsExperience") {
        comparison = (a.yearsExperience || 0) - (b.yearsExperience || 0);
      } else if (sortBy === "status") {
        const aStatus = a.hasAssessment ? (a.isConfirmed ? 2 : 1) : 0;
        const bStatus = b.hasAssessment ? (b.isConfirmed ? 2 : 1) : 0;
        comparison = aStatus - bStatus;
      }

      return sortDirection === "desc" ? -comparison : comparison;
    });

    setFilteredSkills(sortedSkills);
  }, [getFilteredSkillsByTab, sortBy, sortDirection]);

  useEffect(() => {
    sortSkills();
  }, [sortSkills]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.currentTarget.value);
  };

  const handleCategoryChange = (value: string | null) => {
    setSelectedCategory(value);
  };

  const handleCreateAssessment = (skill: SkillMatrixEntry) => {
    setAssessmentSkill(skill);
    setAssessmentMode("create");
    setAssessmentFormSkillLevel(skill.levelSelfRating || 1);
    setAssessmentFormYearsExperience(skill.yearsExperience);
    setAssessmentFormYearsInput(skill.yearsExperience?.toString() || "");
    setAssessmentFormComments(skill.assessmentComments || "");
    setManagerComments("");
    setShowAssessmentModal(true);
  };

  const handleEditAssessment = (skill: SkillMatrixEntry) => {
    setAssessmentSkill(skill);
    setAssessmentMode("edit");
    setAssessmentFormSkillLevel(skill.levelSelfRating || 1);
    setAssessmentFormYearsExperience(skill.yearsExperience);
    setAssessmentFormYearsInput(skill.yearsExperience?.toString() || "");
    setAssessmentFormComments(skill.assessmentComments || "");
    setManagerComments("");
    setShowAssessmentModal(true);
  };

  const handleConfirmAssessment = (skill: SkillMatrixEntry) => {
    setAssessmentSkill(skill);
    setAssessmentMode("confirm");
    setAssessmentFormSkillLevel(skill.levelSelfRating || 1);
    setAssessmentFormYearsExperience(skill.yearsExperience);
    setAssessmentFormYearsInput(skill.yearsExperience?.toString() || "");
    setAssessmentFormComments(skill.assessmentComments || "");
    setManagerComments("");
    setShowAssessmentModal(true);
  };

  const handleCancelAssessment = () => {
    setShowAssessmentModal(false);
    setAssessmentSkill(null);
    setAssessmentFormSkillLevel(1);
    setAssessmentFormYearsExperience(null);
    setAssessmentFormYearsInput("");
    setAssessmentFormComments("");
    setManagerComments("");
  };

  const handleAssessmentFormYearsChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.currentTarget.value;
    setAssessmentFormYearsInput(value);

    if (value.trim() === "") {
      setAssessmentFormYearsExperience(null);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        setAssessmentFormYearsExperience(numValue);
      }
    }
  };

  const handleSaveAssessment = async () => {
    if (!person || !assessmentSkill || !currentPersonId) return;

    setIsProcessingAssessment(true);
    try {
      if (assessmentMode === "create") {
        // Create new assessment as manager
        const managerAssessmentRequest: CreateSkillAssessmentAsManagerRequest =
        {
          personTechnologyId: assessmentSkill.personTechnologyId,
          levelManagerRating: assessmentFormSkillLevel,
          yearsExperience: assessmentFormYearsExperience,
          assessmentComments: assessmentFormComments || null,
          managerComments: managerComments || null,
          status: SkillConfirmationStatus.Approved,
          personId: person.id,
          userId: currentPersonId,
        };

        await skillMatrixService.createAssessmentAsManager(
          managerAssessmentRequest
        );
      } else if (assessmentMode === "edit") {
        // Update existing assessment as manager (creates confirmation with override)
        const updateData = {
          levelSelfRating: assessmentFormSkillLevel,
          yearsExperience: assessmentFormYearsExperience,
          comments: assessmentFormComments || null,
        };

        await skillMatrixService.updateAssessmentAsManager(
          assessmentSkill.skillAssessmentId!,
          updateData,
          person.id,
          currentPersonId
        );
      } else if (assessmentMode === "confirm") {
        // Confirm assessment with possible override
        const confirmationRequest: CreateSkillConfirmationRequest = {
          assessmentId: assessmentSkill.skillAssessmentId!,
          levelManagerRating: assessmentFormSkillLevel,
          managerComments: managerComments || null,
          status: SkillConfirmationStatus.Approved,
          personId: person.id,
        };

        await skillMatrixService.createSkillConfirmation(confirmationRequest);
      }

      // Reload data to get the updated skill matrix
      await loadData();

      // Close modal
      handleCancelAssessment();
    } catch (err) {
      console.error("Error processing assessment:", err);
      const backendMessage = (err as { responseBody?: { error?: string } })?.responseBody?.error;
      setError(
        backendMessage
        ?? (err instanceof Error ? err.message : "Failed to process assessment. Please try again.")
      );
    } finally {
      setIsProcessingAssessment(false);
    }
  };

  const handleBulkConfirm = async () => {
    if (!person || !currentPersonId) return;

    setIsBulkConfirming(true);
    try {
      const bulkRequest: BulkConfirmSkillAssessmentsRequest = {
        userId: currentPersonId,
        personId: person.id,
        managerComments: bulkManagerComments || null,
        status: SkillConfirmationStatus.Approved,
      };

      await skillMatrixService.bulkConfirmSkillAssessments(bulkRequest);

      // Reload data
      await loadData();

      // Close modal
      setShowBulkConfirmModal(false);
      setBulkManagerComments("");
    } catch (err) {
      console.error("Error bulk confirming assessments:", err);
      const backendMessage = (err as { responseBody?: { error?: string } })?.responseBody?.error;
      setError(
        backendMessage
        ?? (err instanceof Error ? err.message : "Failed to bulk confirm assessments. Please try again.")
      );
    } finally {
      setIsBulkConfirming(false);
    }
  };

  const getStatusBadge = (skill: SkillMatrixEntry) => {
    if (!skill.hasAssessment) {
      return (
        <Badge color="gray" size="sm">
          No Assessment
        </Badge>
      );
    }

    if (skill.isConfirmed) {
      return (
        <Badge
          color="green"
          size="sm"
          leftSection={<CheckCircleIcon style={{ width: rem(12) }} />}
        >
          Confirmed
        </Badge>
      );
    }

    if (skill.needsConfirmation) {
      return (
        <Badge
          color="yellow"
          size="sm"
          leftSection={<ClockIcon style={{ width: rem(12) }} />}
        >
          Pending Review
        </Badge>
      );
    }

    if (skill.isRejected) {
      return (
        <Badge color="red" size="sm">
          Rejected
        </Badge>
      );
    }

    return (
      <Badge color="blue" size="sm">
        Assessed
      </Badge>
    );
  };

  const formatYearsOfExperience = (years: number | null): string => {
    if (years === null || years === undefined) return "—";
    if (years === 0) return "< 1 year";
    if (years === 1) return "1 year";
    if (years % 1 === 0) return `${years} years`;
    return `${years.toFixed(1)} years`;
  };

  // Get years of experience from technology experience data (calculated from projects)
  const getTechnologyExperience = (technologyName: string): number | null => {
    const experience = technologyExperience.find(
      (exp) => exp.technologyName === technologyName
    );
    return experience ? experience.yearsOfExperience : null;
  };

  const goBack = () => {
    navigate(-1);
  };

  const pendingCount = skills.filter((skill) => skill.needsConfirmation).length;
  const confirmedCount = skills.filter((skill) => skill.isConfirmed).length;
  const noAssessmentCount = skills.filter(
    (skill) => !skill.hasAssessment
  ).length;

  return (
    <AppShell header={{ height: 60 }} padding="md">
      <AppShell.Header>
        <Header />
      </AppShell.Header>
      <AppShell.Main>
        <Container size="lg" py="xl">
          <Stack gap="md">
            {!skillAssignmentEnabled && (
              <FeatureUnavailableBanner message="Skill assignment is temporarily unavailable — this feature is being rebuilt. Your existing skills are unaffected and still visible below." />
            )}
            <Group justify="space-between">
              <Group gap="md">
                <Button
                  variant="subtle"
                  leftSection={<ArrowLeftIcon width={16} height={16} />}
                  onClick={goBack}
                >
                  Back
                </Button>
                <Stack gap="xs">
                  <Title order={1}>
                    {person
                      ? `${person.firstName} ${person.lastName}'s Skills`
                      : "Person Skills"}
                  </Title>
                  <Text c="dimmed">
                    {isManagerOrAdmin
                      ? "Manager view - Assess, review, and confirm team member skills (1-5 scale)"
                      : "View team member skills and assessments (1-5 scale)"}
                  </Text>
                </Stack>
              </Group>
              <ManagerOrAdmin>
                {pendingCount > 0 && (
                  <Tooltip
                    label="Skill assignment is temporarily unavailable — this feature is being rebuilt."
                    disabled={skillAssignmentEnabled}
                  >
                    <Button
                      leftSection={<CheckIcon style={{ width: rem(16) }} />}
                      onClick={() => setShowBulkConfirmModal(true)}
                      color="green"
                      disabled={!skillAssignmentEnabled}
                    >
                      Confirm All ({pendingCount})
                    </Button>
                  </Tooltip>
                )}
              </ManagerOrAdmin>
            </Group>

            {isLoading ? (
              <Center p="xl">
                <Loader />
              </Center>
            ) : error ? (
              <Alert
                icon={<ExclamationTriangleIcon style={{ width: rem(16) }} />}
                title="Error"
                color="red"
                mb="md"
              >
                {error}
              </Alert>
            ) : (
              <Stack gap="md">
                <Tabs value={activeTab} onChange={setActiveTab}>
                  <Tabs.List>
                    <Tabs.Tab value="all">
                      All Skills ({skills.length})
                    </Tabs.Tab>
                    <ManagerOrAdmin>
                      <Tabs.Tab value="pending" color="yellow">
                        Pending Review ({pendingCount})
                      </Tabs.Tab>
                    </ManagerOrAdmin>
                    <Tabs.Tab value="confirmed" color="green">
                      Confirmed ({confirmedCount})
                    </Tabs.Tab>
                    <ManagerOrAdmin>
                      <Tabs.Tab value="no-assessment" color="gray">
                        No Assessment ({noAssessmentCount})
                      </Tabs.Tab>
                    </ManagerOrAdmin>
                  </Tabs.List>
                </Tabs>

                <Grid>
                  <Grid.Col span={6}>
                    <TextInput
                      placeholder="Search skills..."
                      leftSection={
                        <MagnifyingGlassIcon style={{ width: rem(18) }} />
                      }
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Select
                      placeholder="Filter by category"
                      data={[{ value: "all", label: "All categories" }]}
                      value={selectedCategory}
                      onChange={handleCategoryChange}
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Group gap="xs">
                      <Select
                        placeholder="Sort by"
                        data={[
                          { value: "technology", label: "Skill or Technology" },
                          { value: "skillLevel", label: "Skill Level" },
                          {
                            value: "yearsExperience",
                            label: "Years Experience",
                          },
                          { value: "status", label: "Status" },
                        ]}
                        value={sortBy}
                        onChange={(value) => setSortBy(value as typeof sortBy)}
                        style={{ flex: 1 }}
                      />
                      <ActionIcon
                        variant="subtle"
                        onClick={() =>
                          setSortDirection((prev) =>
                            prev === "asc" ? "desc" : "asc"
                          )
                        }
                        title={`Sort ${sortDirection === "asc" ? "descending" : "ascending"
                          }`}
                      >
                        {sortDirection === "asc" ? (
                          <ChevronUpIcon style={{ width: rem(18) }} />
                        ) : (
                          <ChevronDownIcon style={{ width: rem(18) }} />
                        )}
                      </ActionIcon>
                    </Group>
                  </Grid.Col>
                </Grid>

                <Paper p="md" withBorder>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th
                          style={{ cursor: "pointer", userSelect: "none" }}
                          onClick={() => {
                            if (sortBy === "technology") {
                              setSortDirection((prev) =>
                                prev === "asc" ? "desc" : "asc"
                              );
                            } else {
                              setSortBy("technology");
                              setSortDirection("asc");
                            }
                          }}
                        >
                          <Group gap="xs" justify="space-between">
                            <Text>Skill or Technology</Text>
                            {sortBy === "technology" &&
                              (sortDirection === "asc" ? (
                                <ChevronUpIcon style={{ width: rem(14) }} />
                              ) : (
                                <ChevronDownIcon style={{ width: rem(14) }} />
                              ))}
                          </Group>
                        </Table.Th>
                        <Table.Th
                          style={{ cursor: "pointer", userSelect: "none" }}
                          onClick={() => {
                            if (sortBy === "skillLevel") {
                              setSortDirection((prev) =>
                                prev === "asc" ? "desc" : "asc"
                              );
                            } else {
                              setSortBy("skillLevel");
                              setSortDirection("asc");
                            }
                          }}
                        >
                          <Group gap="xs" justify="space-between">
                            <Text>Skill Level</Text>
                            {sortBy === "skillLevel" &&
                              (sortDirection === "asc" ? (
                                <ChevronUpIcon style={{ width: rem(14) }} />
                              ) : (
                                <ChevronDownIcon style={{ width: rem(14) }} />
                              ))}
                          </Group>
                        </Table.Th>
                        <Table.Th
                          style={{ cursor: "pointer", userSelect: "none" }}
                          onClick={() => {
                            if (sortBy === "yearsExperience") {
                              setSortDirection((prev) =>
                                prev === "asc" ? "desc" : "asc"
                              );
                            } else {
                              setSortBy("yearsExperience");
                              setSortDirection("asc");
                            }
                          }}
                        >
                          <Group gap="xs" justify="space-between">
                            <Text>Years Experience</Text>
                            {sortBy === "yearsExperience" &&
                              (sortDirection === "asc" ? (
                                <ChevronUpIcon style={{ width: rem(14) }} />
                              ) : (
                                <ChevronDownIcon style={{ width: rem(14) }} />
                              ))}
                          </Group>
                        </Table.Th>
                        <Table.Th
                          style={{ cursor: "pointer", userSelect: "none" }}
                          onClick={() => {
                            if (sortBy === "status") {
                              setSortDirection((prev) =>
                                prev === "asc" ? "desc" : "asc"
                              );
                            } else {
                              setSortBy("status");
                              setSortDirection("asc");
                            }
                          }}
                        >
                          <Group gap="xs" justify="space-between">
                            <Text>Status</Text>
                            {sortBy === "status" &&
                              (sortDirection === "asc" ? (
                                <ChevronUpIcon style={{ width: rem(14) }} />
                              ) : (
                                <ChevronDownIcon style={{ width: rem(14) }} />
                              ))}
                          </Group>
                        </Table.Th>
                        <Table.Th>Favorite</Table.Th>
                        <ManagerOrAdmin>
                          <Table.Th>Manager Actions</Table.Th>
                        </ManagerOrAdmin>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {filteredSkills.length === 0 ? (
                        <Table.Tr>
                          <Table.Td colSpan={isManagerOrAdmin ? 6 : 5} align="center">
                            <Stack align="center" gap="md">
                              <Text c="dimmed">
                                {noSkillsFound
                                  ? `${person?.firstName || "This person"
                                  } has no skills recorded yet.`
                                  : `No skills found in the "${activeTab}" category.`}
                              </Text>
                              {noSkillsFound && person && (
                                <Button
                                  variant="outline"
                                  onClick={() =>
                                    navigate(ROUTES.CV_DETAILS(person.id))
                                  }
                                >
                                  View {person.firstName}'s CV
                                </Button>
                              )}
                            </Stack>
                          </Table.Td>
                        </Table.Tr>
                      ) : (
                        filteredSkills.map((skill) => (
                          <Table.Tr key={skill.personTechnologyId}>
                            <Table.Td>
                              <Text fw={500}>{skill.technologyName}</Text>
                              {skill.assessmentComments && (
                                <Text size="xs" c="dimmed" lineClamp={1}>
                                  {skill.assessmentComments}
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              {skill.isConfirmed &&
                                skill.levelManagerRating &&
                                skill.levelManagerRating !== 0 ? (
                                <>
                                  <Rating
                                    value={skill.levelManagerRating}
                                    readOnly
                                    count={5}
                                    emptySymbol={
                                      <StarIcon style={{ width: rem(16) }} />
                                    }
                                  />
                                  {skill.levelSelfRating &&
                                    skill.levelSelfRating !==
                                    skill.levelManagerRating && (
                                      <Text size="xs" c="dimmed">
                                        Employee self-rating:{" "}
                                        {skill.levelSelfRating}/5
                                      </Text>
                                    )}
                                </>
                              ) : (
                                <>
                                  <Rating
                                    value={skill.levelSelfRating || 0}
                                    readOnly
                                    count={5}
                                    emptySymbol={
                                      <StarIcon style={{ width: rem(16) }} />
                                    }
                                  />
                                  {skill.levelManagerRating &&
                                    skill.levelManagerRating !== 0 &&
                                    skill.levelManagerRating !==
                                    skill.levelSelfRating &&
                                    !skill.needsConfirmation ? (
                                    <Text size="xs" c="blue">
                                      Manager: {skill.levelManagerRating}/5
                                    </Text>
                                  ) : null}
                                </>
                              )}
                            </Table.Td>
                            <Table.Td>
                              {formatYearsOfExperience(
                                getTechnologyExperience(skill.technologyName) ?? skill.yearsExperience
                              )}
                            </Table.Td>
                            <Table.Td>
                              {getStatusBadge(skill)}
                              {skill.hasAssessment && skill.assessmentDate && (
                                <Text size="xs" c="dimmed">
                                  {new Date(
                                    skill.assessmentDate
                                  ).toLocaleDateString()}
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              {skill.favourite ? (
                                <HeartIconSolid
                                  style={{
                                    width: rem(18),
                                    color: "var(--mantine-color-red-6)",
                                  }}
                                />
                              ) : (
                                <Text c="dimmed" size="sm">
                                  —
                                </Text>
                              )}
                            </Table.Td>
                            <ManagerOrAdmin>
                              <Table.Td>
                                <Group gap="xs">
                                  {!skill.hasAssessment && (
                                    <Tooltip
                                      label={
                                        skillAssignmentEnabled
                                          ? "Create assessment for this skill"
                                          : "Skill assignment is temporarily unavailable — this feature is being rebuilt."
                                      }
                                    >
                                      <ActionIcon
                                        variant="subtle"
                                        onClick={() =>
                                          handleCreateAssessment(skill)
                                        }
                                        color="blue"
                                        disabled={!skillAssignmentEnabled}
                                      >
                                        <PencilSquareIcon
                                          style={{ width: rem(16) }}
                                        />
                                      </ActionIcon>
                                    </Tooltip>
                                  )}
                                  {skill.hasAssessment && !skill.isConfirmed && (
                                    <Tooltip
                                      label={
                                        skillAssignmentEnabled
                                          ? "Review and confirm assessment"
                                          : "Skill assignment is temporarily unavailable — this feature is being rebuilt."
                                      }
                                    >
                                      <ActionIcon
                                        variant="subtle"
                                        onClick={() =>
                                          handleConfirmAssessment(skill)
                                        }
                                        color="green"
                                        disabled={!skillAssignmentEnabled}
                                      >
                                        <CheckIcon style={{ width: rem(16) }} />
                                      </ActionIcon>
                                    </Tooltip>
                                  )}
                                  {skill.hasAssessment && (
                                    <Tooltip
                                      label={
                                        skillAssignmentEnabled
                                          ? "Edit assessment"
                                          : "Skill assignment is temporarily unavailable — this feature is being rebuilt."
                                      }
                                    >
                                      <ActionIcon
                                        variant="subtle"
                                        onClick={() =>
                                          handleEditAssessment(skill)
                                        }
                                        color="orange"
                                        disabled={!skillAssignmentEnabled}
                                      >
                                        <PencilSquareIcon
                                          style={{ width: rem(16) }}
                                        />
                                      </ActionIcon>
                                    </Tooltip>
                                  )}
                                </Group>
                              </Table.Td>
                            </ManagerOrAdmin>
                          </Table.Tr>
                        ))
                      )}
                    </Table.Tbody>
                  </Table>
                </Paper>
              </Stack>
            )}
          </Stack>
        </Container>

        {/* Assessment Modal */}
        <ManagerOrAdmin>
          <Modal
            opened={showAssessmentModal}
            onClose={handleCancelAssessment}
            title={`${assessmentMode === "create"
              ? "Create Assessment"
              : assessmentMode === "edit"
                ? "Edit Assessment"
                : "Confirm Assessment"
              }: ${assessmentSkill?.technologyName}`}
            size="md"
          >
            <Stack gap="md">
              <Alert
                icon={<InformationCircleIcon style={{ width: rem(16) }} />}
                title={
                  assessmentMode === "confirm"
                    ? "Confirm Assessment"
                    : "Manager Assessment"
                }
                color={assessmentMode === "confirm" ? "green" : "blue"}
              >
                {assessmentMode === "create" &&
                  "Create an assessment for this skill on behalf of the team member."}
                {assessmentMode === "edit" &&
                  "Update the assessment values for this skill."}
                {assessmentMode === "confirm" &&
                  "Review and confirm the assessment. You can override the skill level if needed."}
              </Alert>

              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  Skill Level{" "}
                  {assessmentMode === "confirm"
                    ? "(Manager Rating 1-5)"
                    : "(Assessment 1-5)"}
                </Text>
                <Rating
                  value={assessmentFormSkillLevel}
                  onChange={setAssessmentFormSkillLevel}
                  size="md"
                  count={5}
                />
                {assessmentMode === "confirm" &&
                  assessmentSkill?.levelSelfRating && (
                    <Text size="xs" c="dimmed">
                      Employee self-rating: {assessmentSkill.levelSelfRating}/5
                    </Text>
                  )}
              </Stack>

              <TextInput
                label="Years of Experience"
                placeholder="e.g. 2.5"
                value={assessmentFormYearsInput}
                onChange={handleAssessmentFormYearsChange}
                type="number"
                step="0.1"
                min="0"
                readOnly={assessmentMode === "confirm"}
              />

              <Textarea
                label={
                  assessmentMode === "confirm"
                    ? "Assessment Comments (Read-only)"
                    : "Assessment Comments"
                }
                placeholder="Describe experience and skills with this technology..."
                value={assessmentFormComments}
                onChange={(event) =>
                  setAssessmentFormComments(event.currentTarget.value)
                }
                rows={3}
                maxLength={1000}
                readOnly={assessmentMode === "confirm"}
              />

              {assessmentMode === "confirm" && (
                <Textarea
                  label="Manager Comments"
                  placeholder="Add your comments about this assessment..."
                  value={managerComments}
                  onChange={(event) =>
                    setManagerComments(event.currentTarget.value)
                  }
                  rows={3}
                  maxLength={1000}
                />
              )}

              <Group justify="flex-end" gap="xs">
                <Button variant="default" onClick={handleCancelAssessment}>
                  Cancel
                </Button>
                <Tooltip
                  label="Skill assignment is temporarily unavailable — this feature is being rebuilt."
                  disabled={skillAssignmentEnabled}
                >
                  <Button
                    onClick={handleSaveAssessment}
                    loading={isProcessingAssessment}
                    disabled={!skillAssignmentEnabled}
                    leftSection={<CheckIcon style={{ width: rem(16) }} />}
                    color={assessmentMode === "confirm" ? "green" : "blue"}
                  >
                    {assessmentMode === "create"
                      ? "Create Assessment"
                      : assessmentMode === "edit"
                        ? "Update Assessment"
                        : "Confirm Assessment"}
                  </Button>
                </Tooltip>
              </Group>
            </Stack>
          </Modal>
        </ManagerOrAdmin>

        {/* Bulk Confirm Modal */}
        <ManagerOrAdmin>
          <Modal
            opened={showBulkConfirmModal}
            onClose={() => setShowBulkConfirmModal(false)}
            title={`Bulk Confirm All Assessments for ${person?.firstName}`}
            size="md"
          >
            <Stack gap="md">
              <Alert
                icon={<InformationCircleIcon style={{ width: rem(16) }} />}
                title="Bulk Confirmation"
                color="green"
              >
                This will confirm all {pendingCount} pending assessments using the
                employee's proposed skill levels.
              </Alert>

              <Textarea
                label="Manager Comments (Optional)"
                placeholder="Add comments for all confirmations..."
                value={bulkManagerComments}
                onChange={(event) =>
                  setBulkManagerComments(event.currentTarget.value)
                }
                rows={3}
                maxLength={1000}
              />

              <Group justify="flex-end" gap="xs">
                <Button
                  variant="default"
                  onClick={() => setShowBulkConfirmModal(false)}
                >
                  Cancel
                </Button>
                <Tooltip
                  label="Skill assignment is temporarily unavailable — this feature is being rebuilt."
                  disabled={skillAssignmentEnabled}
                >
                  <Button
                    onClick={handleBulkConfirm}
                    loading={isBulkConfirming}
                    disabled={!skillAssignmentEnabled}
                    leftSection={<CheckIcon style={{ width: rem(16) }} />}
                    color="green"
                  >
                    Confirm All ({pendingCount})
                  </Button>
                </Tooltip>
              </Group>
            </Stack>
          </Modal>
        </ManagerOrAdmin>
      </AppShell.Main>
    </AppShell>
  );
}
